import type { Restaurant } from "@/types";

const TASTE_DETAIL_FIELDS: (keyof Restaurant)[] = [
  "ratingTasteBalance",
  "ratingTasteIngredients",
  "ratingTasteTechnique",
];

const TASTE_GRADE_TABLE = [
  { grade: "S" as const, min: 8.5 },
  { grade: "A" as const, min: 7 },
  { grade: "B" as const, min: 5 },
  { grade: "C" as const, min: 3 },
  { grade: "D" as const, min: 0 },
];

type TasteGrade = NonNullable<Restaurant["ratingTasteGrade"]>;

type TasteAggregate = {
  average5?: number;
  fine9?: number;
  grade?: TasteGrade;
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function parseScore(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return undefined;
}

function sanitizeTasteDetail(partial: Partial<Restaurant>): Partial<Restaurant> {
  const patch: Partial<Restaurant> = {};
  for (const key of TASTE_DETAIL_FIELDS) {
    const raw = parseScore((partial as any)[key]);
    if (raw === undefined) continue;
    patch[key] = clamp(raw, 0, 5);
  }
  return patch;
}

function gradeFromFine(fine: number): TasteGrade {
  const hit = TASTE_GRADE_TABLE.find((entry) => fine >= entry.min);
  return hit ? hit.grade : "D";
}

function computeTasteAggregate(partial: Partial<Restaurant>): TasteAggregate {
  const detailPatch = sanitizeTasteDetail(partial);
  const detailValues = TASTE_DETAIL_FIELDS
    .map((key) => detailPatch[key])
    .filter((v): v is number => typeof v === "number" && v > 0);

  let base: number | undefined;
  if (detailValues.length > 0) {
    base = detailValues.reduce((sum, val) => sum + val, 0) / detailValues.length;
  }

  if (base === undefined) {
    const taste = parseScore((partial as Restaurant).ratingTaste);
    if (typeof taste === "number" && taste > 0) base = taste;
  }

  if (base === undefined) {
    const legacy = parseScore((partial as Restaurant).rating);
    if (typeof legacy === "number" && legacy > 0) base = legacy;
  }

  if (base === undefined) {
    return {};
  }

  const averaged = clamp(base, 0, 5);
  const averageRounded = Math.round(averaged * 10) / 10;
  const fine = Math.round((averaged / 5) * 9 * 10) / 10;
  const fineClamped = clamp(fine, 0, 9);
  const grade = gradeFromFine(fineClamped);

  return { average5: averageRounded, fine9: fineClamped, grade };
}

export function deriveTasteFields(partial: Partial<Restaurant>): Partial<Restaurant> {
  const detailPatch = sanitizeTasteDetail(partial);
  const aggregate = computeTasteAggregate({ ...partial, ...detailPatch });
  const result: Partial<Restaurant> = { ...detailPatch };
  if (aggregate.average5 !== undefined) result.ratingTaste = aggregate.average5;
  if (aggregate.fine9 !== undefined) result.ratingTasteFine = aggregate.fine9;
  if (aggregate.grade !== undefined) result.ratingTasteGrade = aggregate.grade;
  return result;
}

export function tasteAverageScore(r: Restaurant): number {
  const aggregate = computeTasteAggregate(r);
  return aggregate.average5 ?? 0;
}

export function tasteFineScore(r: Restaurant): number {
  const aggregate = computeTasteAggregate(r);
  return aggregate.fine9 ?? 0;
}

export function tasteGrade(r: Restaurant): Restaurant["ratingTasteGrade"] {
  const aggregate = computeTasteAggregate(r);
  return aggregate.grade;
}

// 総合スコア(0..20): 味を重み付け(×2)し、25点満点を20点満点へ正規化。
// legacy rating(0..5)のみ設定されている場合は rating*4 を代替使用する。
export function compositeScore20(r: Restaurant): number {
  const taste = tasteAverageScore(r);
  const others = [r.ratingAmbience, r.ratingService, r.ratingCost]
    .map((v) => {
      const parsed = parseScore(v);
      return typeof parsed === "number" && parsed > 0 ? clamp(parsed, 0, 5) : 0;
    });
  const othersSum = others.reduce((sum, val) => sum + val, 0);
  const weighted = taste * 2 + othersSum;
  if (weighted > 0) {
    const scaled = weighted * 0.8; // 25点満点 → 20点満点
    return Math.round(scaled * 10) / 10;
  }
  const legacy = parseScore(r.rating);
  return typeof legacy === "number" && legacy > 0 ? Math.round(clamp(legacy, 0, 5) * 4) : 0;
}

// 5点刻みで色味を変えるためのテキスト色クラス
export function scoreToneClass(score: number): string {
  if (score >= 15) return "text-green-600";      // 15–20
  if (score >= 10) return "text-primary";        // 10–14
  if (score >= 5)  return "text-amber-600";      // 5–9
  return "text-muted-foreground";                // 0–4
}

// バッジ背景・枠色（視認性向上用）
export function scoreToneBadgeClasses(score: number): string {
  if (score >= 15) return "bg-green-50 border-green-200";    // 15–20
  if (score >= 10) return "bg-primary/10 border-primary/30"; // 10–14
  if (score >= 5)  return "bg-amber-50 border-amber-200";    // 5–9
  return "bg-muted/30 border-border";                         // 0–4
}
